import React, { useMemo } from 'react'

import { Table, TableColumnsType } from 'antd'

import { DownloadLink } from '../shared/DownloadLink'
import { formatBucketTitle } from '../shared/utils'

interface TopSQLComparisonProps {
  data?: {
    summary?: {
      topSqlDigest?: {
        added?: number
        removed?: number
        Stable?: number
        ImprovedPerformance?: number
        DegradedPerformance?: number
        diffDetail?: {
          buckets?: Array<Record<string, number>>
        }
      }
    }
    topSqlTidbDigestDiff?: {
      digestDiff?: any[]
    }
  }
}

interface TopSQLData {
  added: number | null
  removed: number | null
  Stable?: number | null
  ImprovedPerformance: number | null
  DegradedPerformance: number | null
  '[\u003c-50]': number | null
  '[-50~-20]': number | null
  '[-20~-5]': number | null
  '[-5~5]': number | null
  '[5~20]': number | null
  '[20~50]': number | null
  '[\u003e50]': number | null
}

export const TopSQLComparison: React.FC<TopSQLComparisonProps> = ({ data }) => {
  const topSQLData = useMemo<TopSQLData>(() => {
    const result: TopSQLData = {
      added: null,
      removed: null,
      Stable: null,
      ImprovedPerformance: null,
      DegradedPerformance: null,
      '[\u003c-50]': null,
      '[-50~-20]': null,
      '[-20~-5]': null,
      '[-5~5]': null,
      '[5~20]': null,
      '[20~50]': null,
      '[\u003e50]': null
    }

    // 从 summary.topSqlDigest 中提取数据
    if (data?.summary?.topSqlDigest) {
      const digest = data.summary.topSqlDigest
      result.added = digest.added ?? null
      result.removed = digest.removed ?? null
      result.Stable = digest.Stable ?? null
      result.ImprovedPerformance = digest.ImprovedPerformance ?? null
      result.DegradedPerformance = digest.DegradedPerformance ?? null

      // 从 diffDetail.buckets 中提取数据
      if (digest.diffDetail?.buckets) {
        digest.diffDetail.buckets.forEach((bucket) => {
          // bucket 是一个对象，如 { "[<-50]": 0 }
          const key = Object.keys(bucket)[0]
          const value = bucket[key]
          if (key in result) {
            result[key as keyof TopSQLData] = value
          }
        })
      }
    }

    return result
  }, [data])

  // 计算总数
  const total = useMemo(() => {
    return data?.topSqlTidbDigestDiff?.digestDiff?.length ?? 0
  }, [data])

  const columns: TableColumnsType<TopSQLData> = [
    {
      title: `Improved (${topSQLData.ImprovedPerformance ?? 0})`,
      key: 'Improved',
      children: [
        {
          title: formatBucketTitle('[\u003c-50]'),
          dataIndex: '[\u003c-50]',
          key: '[\u003c-50]',
          render: (value) =>
            value !== null && value !== undefined ? value.toLocaleString() : '-'
        },
        {
          title: formatBucketTitle('[-50~-20]'),
          dataIndex: '[-50~-20]',
          key: '[-50~-20]',
          render: (value) =>
            value !== null && value !== undefined ? value.toLocaleString() : '-'
        },
        {
          title: formatBucketTitle('[-20~-5]'),
          dataIndex: '[-20~-5]',
          key: '[-20~-5]',
          render: (value) =>
            value !== null && value !== undefined ? value.toLocaleString() : '-'
        }
      ]
    },
    {
      title: `Stable (${topSQLData.Stable ?? 0})`,
      key: 'Stable',
      children: [
        {
          title: formatBucketTitle('[-5~5]'),
          dataIndex: '[-5~5]',
          key: '[-5~5]',
          render: (value) =>
            value !== null && value !== undefined ? value.toLocaleString() : '-'
        }
      ]
    },
    {
      title: `Degraded (${topSQLData.DegradedPerformance ?? 0})`,
      key: 'Degraded',
      children: [
        {
          title: formatBucketTitle('[5~20]'),
          dataIndex: '[5~20]',
          key: '[5~20]',
          render: (value) =>
            value !== null && value !== undefined ? value.toLocaleString() : '-'
        },
        {
          title: formatBucketTitle('[20~50]'),
          dataIndex: '[20~50]',
          key: '[20~50]',
          render: (value) =>
            value !== null && value !== undefined ? value.toLocaleString() : '-'
        },
        {
          title: formatBucketTitle('[\u003e50]'),
          dataIndex: '[\u003e50]',
          key: '[\u003e50]',
          render: (value) =>
            value !== null && value !== undefined ? value.toLocaleString() : '-'
        }
      ]
    },
    {
      title: 'Total',
      key: 'Total',
      render: () => (total > 0 ? total.toLocaleString() : '-')
    }
  ]

  return (
    <section className='mb-6'>
      <div className='mb-4 flex items-center'>
        <h2 className='text-2xl font-semibold m-0 mr-1'>Top SQL Comparison</h2>
        <DownloadLink data={data} filename='sql_diff.json' />
      </div>
      <Table
        columns={columns}
        dataSource={[topSQLData]}
        rowKey={() => 'top-sql-comparison'}
        pagination={false}
        bordered
        size='small'
      />
    </section>
  )
}
